import React, { PureComponent } from "react"
import { axisTop } from "d3-axis"
import { interpolateNumber, interpolateRound } from "d3-interpolate"
import { scaleLinear } from "d3-scale"
import { select } from "d3-selection"
import "d3-transition" // eslint-disable-line
import d3Tip from "d3-tip"

export default class FlowGraph extends PureComponent {
    constructor(props) {
        super(props)
        this.state = {}
        this.margin = {
            top: 30,
            bottom: 0,
            left: props.shortXAxis ? 70 : 120,
        }
        this.minBarHeight = 25
        this.maxBarHeight = 50
        this.ticks = 3
        this.createGraph = this.createGraph.bind(this)
        this.windowResize = this.windowResize.bind(this)
    }

    componentDidMount() {
        this.renderGraph()
        window.addEventListener("resize", this.windowResize)
    }

    componentDidUpdate(prevProps) {
        this.renderGraph()
    }

    renderGraph() {
        const containerHeight = this.getContainerHeight(
            this.graphContainer,
            this.header,
            this.margin
        )
        if (this.props.flow && this.props.flow.length && containerHeight) {
            this.margin.right = this.calculateRightMargin(this.props.isEditMode)
            const width =
                this.graphContainer.clientWidth -
                this.margin.left -
                this.margin.right
            this.createGraph(this.props.flow, width, containerHeight)
        }
    }

    windowResize() {
        this.renderGraph()
    }

    hideTips() {
        if (this.tip && this.barContainer) {
            const tip = this.tip
            this.barContainer.each(function (d) {
                tip.hide(d, this)
            })
        }
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.windowResize)
        // Make sure to hide any tips since they may accidentally last
        this.hideTips()
    }

    createGraph(data, width, containerHeight) {
        this.hideTips()

        data = data.map((item) => ({
            ...item,
            isEditMode: this.props.isEditMode,
        }))
        let barHeight = this.minBarHeight
        let barPlusSpace = barHeight * 2
        let height = barPlusSpace * data.length

        if (containerHeight > height) {
            // If there is more room to spare, reverse engineer barHeight from containerHeight
            height = containerHeight
            barHeight = Math.min(height / (data.length * 2), this.maxBarHeight)
            barPlusSpace = barHeight * 2
        }

        // Set the dimensions for x according to the data
        // and add the data for the x-axis
        let maxValue = this.props.maxValue
        if (!maxValue) {
            maxValue = data.reduce(
                (previousMax, current) => Math.max(previousMax, current.count),
                0
            )
        }
        const x = scaleLinear().range([0, width]).domain([0, maxValue])

        // Set the top x Axis
        const xAxis = axisTop()
            .scale(x)
            .tickSizeInner(-height)
            .tickSizeOuter(0)
            .ticks(this.ticks)

        // Create the svg element that contains the chart
        let chart = select(this.graphContainer).select("svg").select("g")
        const needsInitialising = chart.empty()
        if (needsInitialising) {
            chart = select(this.graphContainer)
                .append("svg")
                .attr("width", "100%")
                .attr("height", height + this.margin.top + this.margin.bottom)
                .append("g")
                .attr(
                    "transform",
                    `translate(${this.margin.left}, ${this.margin.top})`
                )
        }

        // Add the containers of each bar to the chart, positioned accordingly
        const tip = d3Tip()
            .attr("class", "d3-tip")
            .offset([0, 0])
            .html(({ key, count }) => `${key}: ${count}`)
        this.tip = tip

        const barContainer = chart
            .selectAll(".bar-container")
            .data(data, ({ key }) => key)

        const barContainerEnter = barContainer
            .enter()
            .append("g")
            .classed("bar-container", true)
            .attr(
                "transform",
                (_, index) => `translate(0, ${index * barPlusSpace})`
            )
            .on("mouseover", function (d) {
                tip.show(d, this)
            })
            .on("mouseout", function (d) {
                tip.hide(d, this)
            })

        barContainer
            .transition()
            .duration(1000)
            .attr(
                "transform",
                (_, index) => `translate(0, ${index * barPlusSpace})`
            )

        this.barContainer = barContainer

        barContainer
            .exit()
            .each(function (d) {
                tip.hide(d, this)
            })
            .remove()

        chart.call(tip)

        // Add the bars to the bar chart
        barContainerEnter.append("rect").classed("bar", true).attr("width", 0)
        barContainer
            .merge(barContainerEnter)
            .select(".bar")
            .attr("height", barHeight)
            .classed("highlighted", ({ key }) =>
                this.props.highlightedItems
                    ? this.props.highlightedItems.includes(key)
                    : true
            )
            .transition()
            .delay(this.props.animationDelay || 0)
            .duration(1000)
            .attr("width", (flowItem) => x(flowItem.count))

        // Add the ending bar lines (the anti-bar)
        barContainerEnter
            .append("rect")
            .classed("anti-bar", true)
            .attr("width", 0)
            .attr("x", 0)

        barContainer
            .merge(barContainerEnter)
            .select(".anti-bar")
            .attr("height", barHeight)
            .transition()
            .delay(this.props.animationDelay || 0)
            .duration(1000)
            .attr("width", (flowItem) => width - x(flowItem.count))
            .attr("x", (flowItem) => x(flowItem.count))

        // Add the text
        barContainerEnter
            .append("text")
            .text(0)
            .classed("total-text", true)
            .attr("x", 10)
            .style("opacity", 0)
            .style("fill", "white")

        barContainer
            .merge(barContainerEnter)
            .select(".total-text")
            .attr("transform", `translate(0, ${barHeight / 2 + 5})`)
            .transition()
            .delay(this.props.animationDelay || 0)
            .duration(1000)
            .style("opacity", 1)
            .tween("text", function (flowItem) {
                const that = this
                const isDecimal = flowItem.count > 0 && flowItem.count < 1
                const interpolateFunction = isDecimal
                    ? interpolateNumber
                    : interpolateRound
                const interpolate = interpolateFunction(
                    that.textContent,
                    flowItem.count
                )
                return function (time) {
                    let value = interpolate(time)
                    if (isDecimal) {
                        value = value.toFixed(3)
                    }
                    that.textContent = value
                }
            })

        // Add the text
        let editButtonContainer = barContainer
            .merge(barContainerEnter)
            .selectAll(".edit-button-container")
        if (this.props.isEditMode) {
            if (editButtonContainer.empty()) {
                editButtonContainer = barContainer
                    .merge(barContainerEnter)
                    .append("g")
                    .classed("edit-button-container", true)
                editButtonContainer
                    .append("circle")
                    .style("fill", "transparent")
                    .style("stroke", "#777777")
                    .style("stroke-width", "0.5px")
                    .attr("r", "6px")
                    .style("transform", `translate(14.6px, 15px)`)

                editButtonContainer
                    .append("path")
                    .style("fill", "#777777")
                    .attr(
                        "d",
                        "M17.2,12.29l-2.261,2.264L12.677,12.29l-.387.387,2.264,2.261L12.29,17.2l.387.387,2.261-2.264L17.2,17.586l.387-.387-2.264-2.261,2.264-2.261Z"
                    )
            }
        } else if (!editButtonContainer.empty()) {
            editButtonContainer.remove()
        }

        editButtonContainer
            .attr("x", width)
            .attr("data-title", ({ key }) => key)
            .style(
                "transform",
                `translate(${width - 10}px, -${barHeight / 2 + 5}px) scale(2)`
            )
            .on("click", ({ key }) => this.props.onRemoveItemClick(key))

        // Add the x Axis
        let axis = chart.select(".axis")
        if (axis.empty()) {
            axis = chart.append("g").classed("x", true).classed("axis", true)
        }
        axis.attr("transform", `translate(0, ${-(this.margin.top / 2)})`).call(
            xAxis
        )

        // Add the y Axis
        barContainerEnter
            .append("foreignObject")
            .attr("transform", `translate(-${this.margin.left - 10}, 0)`)
            .attr("width", this.margin.left - 20)
            .attr("height", "100%")
            .append("xhtml:div")
            .text(({ key }) => key)
            .attr("aria-label", ({ key }) => key)
            .classed("total-text", true)
    }

    calculateRightMargin(isEditMode) {
        let margin = 20
        if (isEditMode) {
            // Leaving some space for crosses
            margin += 30
        }
        return margin
    }

    getContainerHeight(container, header, margin) {
        // Take off extra 20 for the x-axis
        return (
            container.clientHeight -
            header.clientHeight -
            margin.top -
            margin.bottom -
            20
        )
    }

    render() {
        let content = null
        if (
            !this.props.isFetching &&
            (!this.props.flow || !this.props.flow.length)
        ) {
            content = "No data"
        }
        return (
            <div className="widget flow-graph">
                <h3
                    className="widget-header"
                    ref={(header) => (this.header = header)}
                >
                    {this.props.renderHeader()}
                </h3>
                <div
                    className="widget-content"
                    ref={(container) => (this.graphContainer = container)}
                >
                    {content}
                </div>
            </div>
        )
    }
}
